// Remove the assignment — the item goes back to the unassigned queue.
//
// Used from the item page and from the row "⋯" menu. It says WHO is being
// removed, that nothing about the work is lost (it stays as a draft anyone
// allowed can assign again), and takes an optional reason that lands in the
// assignment history. When the owner is handing their own work back the copy
// says so — and that the person who created it will be told.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, UserMinus } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { getApiErrorMessage } from '@/lib/apiError';
import { WorkItem, unassignWorkItem } from '@/services/workService';

export const UnassignWorkDialog: React.FC<{
  open: boolean;
  workItemId: string;
  /** Who is being removed (display name). */
  ownerName?: string;
  /** The caller IS the owner: this is a hand-back. */
  selfHandBack?: boolean;
  /** Open sub-items exist: offer to unassign the ones still with this owner. */
  hasOpenSubitems?: boolean;
  /** The item's updated_at as displayed — the server refuses (409) if it changed since. */
  expectedUpdatedAt?: string;
  onClose: () => void;
  onDone: (item: WorkItem) => void;
}> = ({ open, workItemId, ownerName, selfHandBack = false, hasOpenSubitems = false, expectedUpdatedAt, onClose, onDone }) => {
  const { t } = useTranslation('work');
  const [reason, setReason] = useState('');
  const [moveSubtasks, setMoveSubtasks] = useState(true);
  const [busy, setBusy] = useState(false);
  // A fresh decision every time — never a stale reason submitted unread.
  useEffect(() => { if (open) { setReason(''); setMoveSubtasks(true); setBusy(false); } }, [open, workItemId]);

  const close = () => { if (!busy) onClose(); };
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const item = await unassignWorkItem(workItemId, {
        reason: reason.trim() || undefined,
        ...(hasOpenSubitems ? { move_subtasks: moveSubtasks } : {}),
        ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}),
      });
      toast.success((selfHandBack
        ? t('handed_back_ok', { defaultValue: 'Handed back. It is unassigned again.' })
        : t('unassigned_ok', { defaultValue: 'Assignment removed. The work is unassigned again.' })) as string);
      onDone(item);
      onClose();
    } catch (e) {
      // The server's refusal names the state and who can act — shown verbatim.
      toast.error(getApiErrorMessage(e, t('unassign_failed', { defaultValue: 'Could not remove the assignment.' })));
    } finally {
      setBusy(false);
    }
  };

  const who = ownerName || t('unassign_the_assignee', { defaultValue: 'the assignee' });
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {selfHandBack
              ? t('hand_back_title', { defaultValue: 'Hand this work back?' })
              : t('unassign_title', { defaultValue: 'Remove {{name}} from this work?', name: who })}
          </DialogTitle>
          <DialogDescription>
            {selfHandBack
              ? t('hand_back_help', { defaultValue: 'You will no longer be assigned. The work stays as a draft with everything on it, and the person who created it will be told so they can pick someone.' })
              : t('unassign_help', { defaultValue: 'Nothing is deleted: the work goes back to unassigned as a draft — checklist, evidence and comments stay — and can be assigned again. {{name}} is told.', name: who })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor={`unassign-reason-${workItemId}`} className="text-xs">
              {t('unassign_reason', { defaultValue: 'Reason (optional)' })}
            </Label>
            <Textarea id={`unassign-reason-${workItemId}`} rows={2} value={reason} disabled={busy}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('unassign_reason_ph', { defaultValue: 'e.g. assigned to the wrong person. Shown in the history.' }) as string} />
          </div>
          {hasOpenSubitems && (
            <label className="flex items-start gap-2 text-sm text-foreground" htmlFor={`unassign-subtasks-${workItemId}`}>
              <Checkbox id={`unassign-subtasks-${workItemId}`} checked={moveSubtasks} disabled={busy}
                onCheckedChange={(v) => setMoveSubtasks(v === true)} className="mt-0.5" />
              <span>
                {t('unassign_subtasks', { defaultValue: 'Also unassign the open sub-items still assigned to {{name}}', name: who })}
                <span className="block text-xs text-muted-foreground">
                  {t('reassign_move_subtasks_help', { defaultValue: 'Sub-items someone else owns, and finished ones, stay as they are.' })}
                </span>
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={busy}>{t('keep', { defaultValue: 'Keep it' })}</Button>
          <Button variant="destructive" onClick={() => void submit()} disabled={busy}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <UserMinus className="mr-1 h-4 w-4" />}
            {selfHandBack ? t('hand_back', { defaultValue: 'Hand back' }) : t('unassign', { defaultValue: 'Remove assignment' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnassignWorkDialog;
